import {TriangleDownIcon, VersionsIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button} from '@primer/react'
import {useMemo, useState} from 'react'

import {CommitsSelector} from './CommitsSelector'
import type {CommitSelection} from './CommitsSelector'
import type {CommitsSelectorCommitData} from '../../page-data/payloads/file-tree'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {
  getButtonTextFromCommitData,
  getCommitSelectorOptionText,
  ALL_COMMITS_TEXT,
} from '../../helpers/commit-label-helpers'
import {useComparisonInfo, useHasCommitRange, useSelectedRange} from '../../stores/selectors'

const variantProps = {
  default: {
    variant: 'default' as const,
    leadingVisual: VersionsIcon,
  },
  condensed: {
    variant: 'invisible' as const,
    leadingVisual: undefined,
  },
}

export type CommitsDropdownProps = {
  onRangeUpdated: (args: CommitSelection) => void
  commits: CommitsSelectorCommitData[]
  lastReviewOid?: string
  variant?: 'default' | 'condensed'
}

/**
 * Shows a simple dropdown with a few options for filtering commits. Includes a nested
 * commit selector for selecting a range of commits.
 */
export function CommitsDropdown({onRangeUpdated, commits, lastReviewOid, variant = 'default'}: CommitsDropdownProps) {
  const {baseOid: startOid, headOid: endOid} = useSelectedRange()
  const {
    fullDiff: {baseOid: baseRefOid},
  } = useComparisonInfo()
  const hasCommitRange = useHasCommitRange()

  const commitOids = useMemo(() => commits.map(commit => commit.oid), [commits])

  const lastCommitOid = commitOids[commitOids.length - 1]
  const changesSinceLastReviewSelected = !!lastReviewOid && startOid === lastReviewOid && endOid === lastCommitOid
  const [isCommitDropdownOpen, setIsCommitDropdownOpen] = useState(false)
  const [isCommitSelectorOpen, setIsCommitSelectorOpen] = useState(false)

  // `startOid` is the base commit, which is outside of the range. In order
  // to know if the range only contains one commit, we also need to know the
  // OID of the first commit inside the range.

  const firstCommitInRange = useMemo(() => {
    if (!startOid) return
    if (startOid === baseRefOid) return commitOids[0]

    const startIndex = commitOids.findIndex(oid => oid === startOid)
    if (startIndex < 0) return

    return commitOids[startIndex + 1]
  }, [baseRefOid, commitOids, startOid])

  const buttonText = getButtonTextFromCommitData({endOid, startOid, firstCommitInRange})
  const commitSelectorOptionText = getCommitSelectorOptionText({
    endOid,
    firstCommitInRange,
    commitOids,
  })

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const handleOpenCommitSelector = () => {
    sendPullRequestAnalyticsEvent('commits_menu.select_commit', 'COMMITS_MENU_ITEM')
    setIsCommitDropdownOpen(false)
    setIsCommitSelectorOpen(true)
  }

  // the way a leading visual is passed in to the button component can't be conditionally done through CSS, so we
  // have to do this. This also does not update when the window is resized, but it is not worth using a resize observer
  // for something this small.
  const isSmallScreen = (ssrSafeWindow?.innerWidth ?? 1000) < 543

  const handleShowAllCommits = () => {
    // clear out the existing range
    onRangeUpdated({type: 'unfiltered'})
  }

  const handleRangeUpdated = (args: CommitSelection) => {
    setIsCommitSelectorOpen(false)
    onRangeUpdated(args)
  }

  const handleCommitDropdownOpenChange = (open: boolean) => {
    setIsCommitDropdownOpen(open)
    if (open) {
      sendPullRequestAnalyticsEvent('commits_menu.open', 'COMMITS_MENU')
    }
  }

  return (
    <>
      <ActionMenu open={isCommitDropdownOpen} onOpenChange={handleCommitDropdownOpenChange}>
        <ActionMenu.Anchor>
          <Button
            alignContent="start"
            className="flex-shrink-0"
            leadingVisual={isSmallScreen ? undefined : variantProps[variant].leadingVisual}
            size="small"
            variant={variantProps[variant].variant}
            trailingAction={TriangleDownIcon}
          >
            {buttonText}
          </Button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay side="outside-bottom" width="small">
          <ActionList selectionVariant="single">
            <ActionList.Item selected={!hasCommitRange} onSelect={handleShowAllCommits}>
              {ALL_COMMITS_TEXT}
            </ActionList.Item>
            {lastReviewOid && lastCommitOid && lastReviewOid !== lastCommitOid && (
              <ActionList.Item
                selected={changesSinceLastReviewSelected}
                onSelect={() =>
                  handleRangeUpdated({
                    type: 'range',
                    fromPRBase: lastReviewOid === baseRefOid,
                    baseOid: lastReviewOid,
                    endOid: lastCommitOid,
                  })
                }
              >
                Changes since your last review
              </ActionList.Item>
            )}
            <ActionList.Divider />
            <ActionList.Item
              selected={hasCommitRange && !changesSinceLastReviewSelected}
              onSelect={handleOpenCommitSelector}
            >
              {commitSelectorOptionText}
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {isCommitSelectorOpen && (
        <CommitsSelector
          baseRefOid={baseRefOid || ''}
          commitOids={commitOids}
          commits={commits}
          endOid={endOid}
          startOid={startOid}
          onClose={() => setIsCommitSelectorOpen(false)}
          onRangeUpdated={handleRangeUpdated}
        />
      )}
    </>
  )
}

try{ CommitsDropdown.displayName ||= 'CommitsDropdown' } catch {}